import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { LinkedDocumentsNavigationResult } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  type AssetSearchItem,
  type ChartSearchItem,
  type DocumentSearchItem,
  documentTableRowToSearchItem,
  isAssetSearchItem,
  isChartSearchItem,
  isDocumentSearchItem,
  isSensorSearchItem,
  RecentItem,
  SearchableItem,
  type SensorSearchItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import { searchableItemSchema } from 'features/knowledgeDocumentViewer/types/searchableItemSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { z } from 'zod'

export const MAX_RECENT_ITEMS = 20

const STORAGE_KEY_PREFIX = 'gecko_recent_items'

const recentItemSchema = z.object({
  item: searchableItemSchema,
  accessedAt: z.string(),
})

function getStorageKey(userId: string, siteId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}_${siteId}`
}

function parseStoredRecentItems(raw: string | null): RecentItem[] {
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw)
    const result = z.array(recentItemSchema).safeParse(parsed)
    return result.success ? result.data : []
  } catch (error) {
    console.warn('Failed to read recent items from localStorage:', error)
    return []
  }
}

/**
 * Get all recent items from localStorage.
 * Unrecognised formats (including legacy data) are discarded.
 */
export function getRecentItems(userId: string, siteId: string): RecentItem[] {
  try {
    const key = getStorageKey(userId, siteId)
    return parseStoredRecentItems(localStorage.getItem(key))
  } catch (error) {
    console.warn('Failed to read recent items from localStorage:', error)
    return []
  }
}

export function addRecentItem(
  item: SearchableItem,
  userId: string,
  siteId: string
): RecentItem[] {
  try {
    const items = getRecentItems(userId, siteId)

    const filtered = items.filter((r) => r.item.id !== item.id)

    const newRecentItem: RecentItem = {
      item,
      accessedAt: new Date().toISOString(),
    }

    const updatedItems = [newRecentItem, ...filtered].slice(0, MAX_RECENT_ITEMS)

    const key = getStorageKey(userId, siteId)
    localStorage.setItem(key, JSON.stringify(updatedItems))

    return updatedItems
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old items')
      clearRecentItems(userId, siteId)
    } else {
      console.warn('Failed to save recent item to localStorage:', error)
    }
    return getRecentItems(userId, siteId)
  }
}

/**
 * Remove a single item (by id) from the recents. Used when the underlying
 * entity is deleted so its card cannot dead-end on an unresolvable open.
 */
export function removeRecentItem(
  itemId: string,
  userId: string,
  siteId: string
): RecentItem[] {
  try {
    const items = getRecentItems(userId, siteId)
    const filtered = items.filter((r) => r.item.id !== itemId)
    if (filtered.length === items.length) return items

    const key = getStorageKey(userId, siteId)
    localStorage.setItem(key, JSON.stringify(filtered))
    return filtered
  } catch (error) {
    console.warn('Failed to remove recent item from localStorage:', error)
    return getRecentItems(userId, siteId)
  }
}

export function getRecentsByType(
  userId: string,
  siteId: string,
  itemType: 'document'
): RecentItem<DocumentSearchItem>[]
export function getRecentsByType(
  userId: string,
  siteId: string,
  itemType: 'sensor'
): RecentItem<SensorSearchItem>[]
export function getRecentsByType(
  userId: string,
  siteId: string,
  itemType: 'asset'
): RecentItem<AssetSearchItem>[]
export function getRecentsByType(
  userId: string,
  siteId: string,
  itemType: 'chart'
): RecentItem<ChartSearchItem>[]
export function getRecentsByType(
  userId: string,
  siteId: string,
  itemType: SearchableItem['itemType']
): RecentItem[] {
  const items = getRecentItems(userId, siteId)
  switch (itemType) {
    case 'document':
      return items.filter((recent): recent is RecentItem<DocumentSearchItem> =>
        isDocumentSearchItem(recent.item)
      )
    case 'sensor':
      return items.filter((recent): recent is RecentItem<SensorSearchItem> =>
        isSensorSearchItem(recent.item)
      )
    case 'asset':
      return items.filter((recent): recent is RecentItem<AssetSearchItem> =>
        isAssetSearchItem(recent.item)
      )
    case 'chart':
      return items.filter((recent): recent is RecentItem<ChartSearchItem> =>
        isChartSearchItem(recent.item)
      )
  }
}

export function clearRecentItems(userId: string, siteId: string): void {
  try {
    const key = getStorageKey(userId, siteId)
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to clear recent items:', error)
  }
}

export function getRecentItemsStorageKey(
  userId: string,
  siteId: string
): string {
  return getStorageKey(userId, siteId)
}

export function resolveRecentItemsForActiveDocuments({
  recentItems,
  activeDocuments,
  workOrderDocuments = [],
}: {
  recentItems: RecentItem[]
  activeDocuments: SystemDocumentEntry[]
  workOrderDocuments?: DocumentTableRow[]
}): RecentItem[] {
  const workOrdersById = new Map(workOrderDocuments.map((row) => [row.id, row]))
  const activeDocumentByBqId = new Map<string, SystemDocumentEntry>()
  const activeDocumentByEntryId = new Map<string, SystemDocumentEntry>()
  for (const entry of activeDocuments) {
    activeDocumentByEntryId.set(entry.entry.id, entry)
    const bqId = getSystemDocumentBqId(entry)
    if (bqId) activeDocumentByBqId.set(bqId, entry)
  }

  return recentItems.flatMap((recent) => {
    if (!isDocumentSearchItem(recent.item)) {
      return [recent]
    }

    const workOrder = workOrdersById.get(recent.item.id)
    if (workOrder)
      return [{ ...recent, item: documentTableRowToSearchItem(workOrder) }]
    const activeDocument =
      activeDocumentByBqId.get(recent.item.id) ??
      activeDocumentByEntryId.get(recent.item.id) ??
      (recent.item.documentData.systemEntryId
        ? activeDocumentByEntryId.get(recent.item.documentData.systemEntryId)
        : undefined)
    if (!activeDocument) return []

    return [
      {
        ...recent,
        item: documentTableRowToSearchItem(
          transformSystemDocumentToTableRow(activeDocument)
        ),
      },
    ]
  })
}

export function linkedDocsNavigationToRecentAsset({
  documentIds,
  referenceText,
}: LinkedDocumentsNavigationResult): AssetSearchItem | null {
  if (!referenceText) return null
  return {
    id: `box-${referenceText}`,
    itemType: 'asset',
    displayName: referenceText,
    name: referenceText,
    hasLinkedDatapoints: false,
    linkedDatapointIds: [],
    linkedDocumentIds: documentIds,
    linkedDocumentCount: documentIds.length,
  }
}
